import { Component, HostListener, OnInit, OnDestroy, Inject, PLATFORM_ID, Renderer2, DOCUMENT } from '@angular/core';
import { ProfileMenu } from '../../shared/components/profile-menu/profile-menu';
import { NotificationsPopup } from '../../shared/components/notifications/notifications-popup/notifications-popup';
import { MobileMenu } from '../mobile-menu/mobile-menu';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  UserProfileService,
  UserProfile,
} from '../../shared/services/user-profile/user-profile.service';
import { Subscription } from 'rxjs';
import { AuthService } from '../../shared/services/auth/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { NotificationService } from '../../shared/services/notifications/notification.service';
import { NotificationPollingService } from '../../shared/services/notifications/notification-polling.service';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CommonService } from '../../shared/services/common-service';
import { LucideAngularModule, Menu } from 'lucide-angular';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [ProfileMenu, NotificationsPopup, MobileMenu, CommonModule, LucideAngularModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit, OnDestroy {
  showNotifications = false;
  mobileMenuOpen = false;
  userProfile: UserProfile | null = null;
  private subscriptions = new Subscription();
  private destroy$ = new Subject<void>();
  unreadCount = 0;
  private isLoadingUnreadCount = false; // Flag to prevent duplicate calls
  readonly Menu = Menu;

  constructor(
    private router: Router,
    private userProfileService: UserProfileService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private notificationPollingService: NotificationPollingService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private _commonService: CommonService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // Close popup when navigating away to prevent it from staying open
    this.subscriptions.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        if (this.showNotifications) {
          setTimeout(() => {
            this.showNotifications = false;
          }, 100);
        }
      }),
    );
  }

  userProfileLoading = false;

  ngOnInit(): void {
    // Subscribe to user profile from shared service
    this.subscriptions.add(
      this.userProfileService.getUserProfile().subscribe((profile) => {
        this.userProfile = profile;
      }),
    );

    // Subscribe to loading state
    this.subscriptions.add(
      this.userProfileService.isLoading().subscribe((loading) => {
        this.userProfileLoading = loading;
      }),
    );

    // Fetch profile immediately on init if authenticated (handles page refresh)
    if (isPlatformBrowser(this.platformId) && this.authService.isAuthenticated()) {
      this.userProfileService.fetchUserProfile(true).subscribe();
      this.loadUnreadCount();
      
      // Start optimized polling for notification counts (30 seconds interval)
      this.notificationPollingService.startPolling(30000);
      
      // Subscribe to polling count updates
      this.subscriptions.add(
        this.notificationPollingService.counts$
          .pipe(takeUntil(this.destroy$))
          .subscribe((counts) => {
            this.unreadCount = counts.unread_count;
          }),
      );
    }

    // Fetch profile on every route navigation to ensure it's always up to date
    // Note: Unread count is only refreshed on init and when notifications are updated
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          debounceTime(100) // Debounce to prevent rapid successive calls
        )
        .subscribe(() => {
          // Fetch profile on each navigation if authenticated
          // This ensures profile is loaded even if backend was down and came back online
          if (isPlatformBrowser(this.platformId) && this.authService.isAuthenticated()) {
            // Use forceRefresh=false to respect cache, but will fetch if cache expired or profile missing
            this.userProfileService.fetchUserProfile(false).subscribe();
            // Removed loadUnreadCount() - only refresh when needed (on init, when opening popup, or when notifications are updated)
          }
        }),
    );
  }


  private loadUnreadCount() {
    if (!isPlatformBrowser(this.platformId) || !this.authService.isAuthenticated()) {
      return;
    }
    if (this._commonService.isAuthCallbackRoute()) {
      return;
      
    }

    // Prevent duplicate calls
    if (this.isLoadingUnreadCount) {
      return;
    }

    this.isLoadingUnreadCount = true;
    this.notificationService
      .getUnreadCount(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.unreadCount = count;
          this.isLoadingUnreadCount = false;
        },
        error: (err) => {
          console.error('Error loading unread count:', err);
          this.unreadCount = 0;
          this.isLoadingUnreadCount = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    
    // Stop polling when component is destroyed
    // Note: Polling service is singleton, so we only stop if this is the last component using it
    // In a real app, you might want to track usage count
  }

  /**
   * Get user's display name (truncated to max 20 characters)
   */
  get displayName(): string {
    // Don't show anything while loading
    if (this.userProfileLoading || !this.userProfile) {
      return '';
    }

    const fullName = this.userProfile.full_name || '';
    const maxLength = 20;

    if (fullName.length <= maxLength) {
      return fullName;
    }

    // Truncate and add ellipsis
    return fullName.substring(0, maxLength) + '...';
  }

  navigateToDashboard(): void {
    // Redirect to dashboard when logo is clicked
    this.router.navigate(['/dashboard']);
  }

  notifications: any[] = [];

  toggleNotifications(event: MouseEvent) {
    event.stopPropagation();
    this.showNotifications = !this.showNotifications;
    // Refresh notifications when opening popup
    if (this.showNotifications) {
      this.loadUnreadCount();
    }
  }

  get hasUnreadNotifications(): boolean {
    return this.unreadCount > 0;
  }

  closeNotifications() {
    this.showNotifications = false;
  }

  markAllAsRead() {
    // This will be handled by the notifications-popup component
    // Just refresh the count after marking all as read
    this.loadUnreadCount();
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    // Prevent body scroll when menu is open using Angular's Renderer2
    if (isPlatformBrowser(this.platformId)) {
      const body = this.document.body;
      if (this.mobileMenuOpen) {
        this.renderer.addClass(body, 'mobile-menu-open');
      } else {
        this.renderer.removeClass(body, 'mobile-menu-open');
      }
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    if (isPlatformBrowser(this.platformId)) {
      // Use Angular's Renderer2 for DOM manipulation
      this.renderer.removeClass(this.document.body, 'mobile-menu-open');
    }
  }

  // ✅ Close popup when clicking outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    // Don't close if popup is not visible
    if (!this.showNotifications) {
      return;
    }

    const target = event.target as HTMLElement;
    // Check if click is outside both the bell and the popup
    if (!target.closest('.notif-bell') && !target.closest('app-notifications-popup')) {
      this.showNotifications = false;
    }
  }
}
